<template>
  <div
    id="date-input"
  >
    <v-layout
      :class="{'input-group--error': errors.length > 0}"
      class="mt-1"
      row
    >
      <v-flex>
        <v-text-field
          :id="id"
          v-model="myDate"
          :autofocus="autofocus"
          :disabled="disabled"
          :error="errors.length > 0"
          :label="label"
          :aria-label="label"
          :placeholder="placeholder"
          :required="required"
          autocomplete="nope"
          class="input--date sel_date_input"
          hint="MM-DD-YYYY"
          type="tel"
          @blur="validateAndFormatDate()"
          @keyup="formatDate()"
          @focus="clearErrors();setFocus()"
          @keydown.native="preventInput($event)"
        />
      </v-flex>
    </v-layout>
    <v-layout
      v-if="errors.length > 0"
      row
    >
      <v-flex
        class="v-messages v-messages__message error--text"
        style="margin-top:-12px;"
        xs12
      >
        {{ errors[0] }}
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
export default {
  name: 'DateInput',

  props: {
    ageRestriction: {
      type: Number,
      default: -1,
      required: false
    },
    autofocus: {
      type: Boolean,
      default: false,
      required: false
    },
    date: {
      type: String,
      default: '',
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    id: {
      type: String,
      default: 'date-input',
      required: false
    },
    label: {
      type: String,
      default: '',
      required: false
    },
    max: {
      type: [String, Number],
      default: null,
      required: false
    },
    min: {
      type: [String, Number],
      default: null,
      required: false
    },
    placeholder: {
      type: String,
      default: '',
      required: false
    },
    required: {
      type: Boolean,
      default: false,
      required: false
    },
    revalidate: {
      type: Boolean,
      default: false,
      required: false
    },
    watchDate: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      myDate: '',
      errors: []
    }
  },

  watch: {
    myDate: function (val, oldVal) {
      this.$emit('input', this.myDate)
    },
    revalidate (newValue, oldValue) {
      if (newValue) {
        this.validateAndFormatDate()
      }
    },
    date (val, oldVal) {
      // used when updating the date externally, like on the mobile 1003
      if (this.watchDate || oldVal == null) { // use double equals to catch null and undefined
        const [f1, f2, f3] = this.date.split('-')
        if (f1.length === 4) {
          this.myDate = `${f2}-${f3}-${f1}`
        } else {
          this.myDate = val
        }
        this.clearErrors()
        this.validateAndFormatDate()
      }
    }
  },

  created: function () {
    this.myDate = null
    if (this.date !== null) {
      const [f1, f2, f3] = this.date.split('-')
      if (f1.length === 4) {
        this.myDate = `${f2}-${f3}-${f1}`
      } else {
        this.myDate = this.date
      }
    }
  },

  methods: {
    clearErrors: function () {
      this.$emit('error', false)
      this.errors = []
    },

    clearFocus: function () {
      this.$emit('clearFocusedField', true)
    },

    formatDate () {
      let num = this.myDate !== null ? this.myDate.replace(/\D/g, '') : ''
      if (parseInt(num[0]) > 1) {
        num = '0' + num
      }
      if (num.length > 8) {
        num = num.slice(0, 8)
      }
      if (parseInt(num[2]) > 3) {
        num = num.slice(0, 2) + '0' + num.slice(2)
      }
      if (num.length > 4) {
        num = `${num.slice(0, 2)}-${num.slice(2, 4)}-${num.slice(4)}`
      } else if (num.length > 2) {
        num = `${num.slice(0, 2)}-${num.slice(2)}`
      }
      this.myDate = num
    },

    preventInput (event) {
      if (event.key === 'Backspace') { // The mobile apps don't return a code so this is so the backspace works there
        return true
      } else if (event.code &&
          event.code !== undefined &&
          event.code !== null &&
          (event.code === 'Backspace' ||
          event.code === 'Delete' ||
          event.code === 'Tab' ||
          event.code.includes('Shift') ||
          event.code.includes('Arrow'))) {
        return true
      } else if (this.myDate && this.myDate.length >= 10) {
        event.preventDefault()
      } else if (!isNaN(parseInt(event.key))) {
        return true
      } else {
        event.preventDefault()
      }
    },

    setFocus: function () {
      this.$emit('setFocusedField', true)
    },

    validateAge () {
      const birthday = this.$moment(this.myDate, 'MM-DD-YYYY')
      return this.$moment().diff(birthday, 'years') >= this.ageRestriction
    },

    getMonth (month) {
      let newMonth = month
      if (month && month.length === 1) {
        newMonth = '0' + month
      }
      if (month && parseInt(month) > 12) {
        newMonth = '12'
      }
      if (month && parseInt(month) <= 0) {
        newMonth = '01'
      }
      return newMonth
    },

    getDay (month, day) {
      const daysInMonth = [null, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
      let newDay = day
      if (month && day && parseInt(day) > daysInMonth[parseInt(month)]) {
        newDay = daysInMonth[parseInt(month)]
      }
      if (day && parseInt(day) <= 0) {
        newDay = '01'
      }
      if (day && day.length === 1) {
        newDay = '0' + day
      }
      if (day === '0') {
        newDay = '01'
      }
      return newDay
    },

    validateMinDate (day, month, year) {
      if (this.min && this.min !== '') {
        if (year && year.length && month && month.length && day && day.length) {
          const date = new Date(year, month - 1, day)
          let startDate = null

          if (typeof this.min === 'string' && this.min.split('-').length >= 3) {
            // Must pass in date as separate params or else Date will assume GMT
            const startYear = this.min.split('-')[0]
            const startMonth = `${parseInt(this.min.split('-')[1]) - 1}`
            const startDay = this.min.split('-')[2]

            startDate = new Date(startYear, startMonth, startDay)
          } else if (typeof this.min === 'number' && this.min > -1) {
            // For old loan app format
            startDate = new Date(`${this.min}`, '00', '01')
          } else {
            this.errors.push('Invalid minimum date for loan application template. Please contact your loan officer')
            this.$emit('error', 'Invalid start date for loan application template. Please contact your loan officer.')
          }
          if (date < startDate) {
            this.errors.push(`Please enter a date after ${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`)
            this.$emit('error', `Please enter a date after ${startDate.getMonth() + 1}/${startDate.getDate()}/${startDate.getFullYear()}`)
          }
        }
      }
    },

    validateMaxDate (day, month, year) {
      if (this.max && this.max !== '') {
        if (year && year.length && month && month.length && day && day.length) {
          const date = new Date(year, month - 1, day)
          let endDate = null

          if (typeof this.max === 'string' && this.max.split('-').length >= 3) {
            // Must pass in date as seporate params or else Date will assume GMT
            const endYear = this.max.split('-')[0]
            const endMonth = `${parseInt(this.max.split('-')[1]) - 1}`
            const endDay = this.max.split('-')[2]

            endDate = new Date(endYear, endMonth, endDay)
          } else if (typeof this.max === 'number' && this.max > -1) {
            // For old loan app format
            endDate = new Date(`${this.max}`, '11', '31')
          } else {
            this.errors.push('Invalid end date for loan application template. Please contact your loan officer')
            this.$emit('error', 'Invalid end date for loan application template. Please contact your loan officer')
          }

          if (date >= endDate) {
            this.errors.push(`Please enter a date before ${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`)
            this.$emit('error', `Please enter a date before ${endDate.getMonth() + 1}/${endDate.getDate()}/${endDate.getFullYear()}`)
          }
        }
      }
    },

    dateValueUndefined (day, month, year) {
      if (day === undefined || month === undefined || year === undefined) return true
      else return false
    },

    validatePresenceAndFormatOfDate (day, month, year) {
      if (this.required === true && this.dateValueUndefined(day, month, year)) {
        this.errors.push('This is a required field')
        this.$emit('error', 'This is a required field')
      } else if (this.myDate && this.myDate.length > 0 && this.required !== true && (this.dateValueUndefined(day, month, year) && (month !== '' || day !== '' || year !== ''))) {
        this.errors.push('You must include month, day, and year')
        this.$emit('error', 'You must include month, day, and year')
      } else if (month && day && year && month.length > 0 && day.length > 0 && year.length > 0 && year.length !== 4) {
        this.errors.push('Please enter a 4-digit year')
        this.$emit('error', 'Please enter a 4-digit year')
      } else {
        this.$emit('error', [])
      }
    },

    validateAndFormatDate: function () {
      if (this.myDate !== null) {
        var [month, day, year] = this.myDate.split('-')
      } else {
        [month, day, year] = [undefined, undefined, undefined]
      }

      month = this.getMonth(month)
      day = this.getDay(month, day)

      this.validatePresenceAndFormatOfDate(day, month, year)
      this.validateMinDate(day, month, year)
      this.validateMaxDate(day, month, year)

      if (this.ageRestriction > 0 && !this.validateAge()) {
        this.errors.push(`You must be ${this.ageRestriction} years or older`)
        this.$emit('error', `You must be ${this.ageRestriction} years or older`)
      }
      if (month && day && year) {
        this.myDate = [month, day, year].join('-')
      } else if (month && day) {
        this.myDate = [month, day].join('-')
      } else if (month) {
        this.myDate = month
      }
    }
  }
}
</script>

<style scoped>
  .input--date {
    width:140px;
  }
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
</style>
